import type { ReactNode } from 'react';

interface ReportScopeFiltersProps {
  period: string;
  onPeriodChange: (value: string) => void;
  periodOptions: Array<{ value: string; label: string }>;
  clinicId?: string;
  onClinicChange?: (value: string) => void;
  clinicOptions?: Array<{ value: string; label: string }>;
  showClinic?: boolean;
  showPeriod?: boolean;
  hint?: string;
  title?: string;
  periodLabel: string;
  clinicLabel: string;
  children?: ReactNode;
  /**
   * `toolbar` — barra compacta del propio reporte (default).
   * `inline` — fila mínima para export en el hub.
   */
  variant?: 'toolbar' | 'inline';
}

const selectClass =
  'h-9 w-full min-w-0 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-2.5 text-sm font-medium text-[var(--color-text-on-surface)] shadow-sm outline-none transition focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20';

export function ReportScopeFilters({
  period,
  onPeriodChange,
  periodOptions,
  clinicId,
  onClinicChange,
  clinicOptions = [],
  showClinic = true,
  showPeriod = true,
  hint,
  title,
  periodLabel,
  clinicLabel,
  children,
  variant = 'toolbar',
}: ReportScopeFiltersProps) {
  const isInline = variant === 'inline';

  return (
    <section
      aria-label={title || periodLabel}
      className={
        isInline
          ? 'flex w-full flex-wrap items-end gap-2.5'
          : 'bi-filter-bar items-end sm:gap-4'
      }
    >
      {showPeriod && (
        <label className={`flex min-w-0 flex-col gap-1 ${isInline ? 'w-[11.5rem] sm:w-44' : 'w-full sm:w-52'}`}>
          <span className="text-[11px] font-medium text-gray-500 dark:text-gray-400">{periodLabel}</span>
          <select
            value={period}
            onChange={(event) => onPeriodChange(event.target.value)}
            className={selectClass}
          >
            {periodOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
      )}

      {showClinic && (
        <label className={`flex min-w-0 flex-col gap-1 ${isInline ? 'w-[12rem] sm:w-52' : 'w-full sm:w-60'}`}>
          <span className="text-[11px] font-medium text-gray-500 dark:text-gray-400">{clinicLabel}</span>
          {clinicOptions.length <= 1 ? (
            <div className="flex h-9 items-center truncate rounded-lg border border-[var(--color-border)] bg-[var(--color-surface-muted)] px-2.5 text-sm text-[var(--color-text-on-surface)]">
              {clinicOptions[0]?.label ?? '—'}
            </div>
          ) : (
            <select
              value={clinicId}
              onChange={(event) => onClinicChange?.(event.target.value)}
              className={selectClass}
            >
              {clinicOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          )}
        </label>
      )}

      {children}

      {hint && !isInline && (
        <p className="w-full basis-full text-xs text-gray-500 dark:text-gray-400">{hint}</p>
      )}
    </section>
  );
}
